import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {HttpContext} from '@angular/common/http';
import {AuthService} from '../auth/auth.service';
import {SentryService} from '../sentry/sentry.service';
import {UsageTelemetryService} from '../../bfa-api/services/usage-telemetry.service';
import {UsageTelemetryData} from '../../bfa-api/models/usage-telemetry-data';
import {SystemInfo} from '../../models/system-info.model';
import {AUTH_TOKEN_HTTP_CONTEXT} from '../../constants/auth-token-http-context.constant';

@Injectable({
    providedIn: 'root'
})
export class TelemetryService {
    private readonly authService = inject(AuthService);
    private readonly usageTelemetryService = inject(UsageTelemetryService);
    private readonly sentryService = inject(SentryService);

    /**
     * Sends usage telemetry data to the backend.
     * Failures are reported to Sentry and never propagated to the caller.
     *
     * @param {SystemInfo} systemInfo The system information used to build the telemetry payload.
     * @return {Promise<void>} A promise that resolves once the telemetry request completed (successfully or not).
     */
    public async send(systemInfo: SystemInfo): Promise<void> {
        try {
            const telemetryData: UsageTelemetryData = {
                apiVersion: systemInfo.apiVersion,
                appType: systemInfo.appType,
                appVersion: systemInfo.appVersion,
                browser: systemInfo.browser,
                browserVersion: systemInfo.browserVersion,
                device: systemInfo.device,
                osVersion: systemInfo.osVersion,
                os: systemInfo.os,
                screenResolution: systemInfo.screenResolution,
            };

            const token = await this.authService.getToken();
            const response = await firstValueFrom(this.usageTelemetryService.usageTelemetryAddUsageTelemetry$Response({
                body: telemetryData,
            }, new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)));

            if (response?.status >= 400) {
                this.sentryService.captureException(new Error(`Sending telemetry data failed. Status: ${response?.status}, body: ${response?.body}`));
            }
        } catch (error: unknown) {
            this.sentryService.captureException(error, 'Sending telemetry data failed.');
        }
    }
}
